import type { Router } from 'vue-router'

/**
 * Recovery from stale route chunks after a deploy.
 *
 * Vite content-hashes every lazily-loaded route chunk, and publishing a new version replaces
 * the asset set — so a tab that loaded the app before the deploy still holds the old module
 * graph and its chunk URLs now 404. Without a handler the failed navigation surfaces only as
 * an unhandled rejection: the route never changes and nothing is shown, so the app reads as
 * frozen when all it needs is a reload.
 *
 * This is the shape Vite documents (listen, then reload) plus the reload limit the community
 * settled on: https://vite.dev/guide/build.html#load-error-handling
 *
 * It assumes a reload fetches a fresh index.html. As of 2026-07-31 the CDN serves it with
 * `s-maxage=31536000`, so an edge holding a stale copy defeats that — and no client-side
 * workaround can fix it (query strings are not in the cache key either). That header is the
 * real fix; this module only covers the tab whose module graph is out of date.
 */

/** Message fragments each engine uses for a failed dynamic import. */
const STALE_ASSET_SIGNATURES = [
	'failed to fetch dynamically imported module', // Chrome, Edge
	'error loading dynamically imported module', // Firefox
	'importing a module script failed', // Safari
	'unable to preload css for', // Vite's CSS preload helper
]

/** Failures further apart than this are separate incidents, so a later deploy retries. */
export const INCIDENT_WINDOW_MS = 30_000

const RELOAD_MARKER = 'staleAssetReloadAt'

/**
 * Whether `error` is a module that could not be fetched, as opposed to anything else a
 * navigation can reject with (a guard redirect, an AppSync failure, a genuine bug).
 */
export function isStaleAssetError(error: unknown): boolean {
	const message = error instanceof Error ? error.message : typeof error === 'string' ? error : ''
	if (!message) return false

	const haystack = message.toLowerCase()
	return STALE_ASSET_SIGNATURES.some((signature) => haystack.includes(signature))
}

/**
 * Whether to reload, given when this tab last reloaded to recover (null if never).
 *
 * A second failure straight after a reload means reloading isn't the answer — the served
 * index.html is stale too — so stop rather than loop. Outside the window it's a new incident
 * (a later deploy in a long-lived tab) and gets its own attempt.
 *
 * `now` is a parameter so the decision stays pure and testable.
 */
export function shouldReload(now: number, lastAttempt: number | null): boolean {
	if (lastAttempt === null || !Number.isFinite(lastAttempt)) return true
	return now - lastAttempt >= INCIDENT_WINDOW_MS
}

function readLastAttempt(): number | null {
	try {
		const raw = sessionStorage.getItem(RELOAD_MARKER)
		if (!raw) return null
		const parsed = Number(raw)
		return Number.isFinite(parsed) ? parsed : null
	} catch {
		// Storage can throw (privacy modes, disabled cookies). Treating it as absent keeps
		// recovery working; the cost is that the loop guard can't trip.
		return null
	}
}

function markAttempt(now: number): void {
	try {
		sessionStorage.setItem(RELOAD_MARKER, String(now))
	} catch {
		// Non-fatal — see readLastAttempt.
	}
}

/**
 * Install the recovery handlers. Call once, as early as possible.
 *
 * Navigating to the destination rather than reloading is the one deviation from the Vite
 * example, and it's deliberate: the failed navigation never changed the URL, so a plain
 * reload would land the user back where they started and silently discard the click.
 *
 * `onRecoveryExhausted` runs when reloading has already been tried and failed. Passed in
 * rather than imported so this module stays independent of the UI (and directly testable).
 */
export function installStaleAssetRecovery(router: Router, onRecoveryExhausted: () => void): void {
	const recover = (fullPath: string) => {
		const now = Date.now()
		if (!shouldReload(now, readLastAttempt())) {
			onRecoveryExhausted()
			return
		}
		markAttempt(now)
		window.location.assign(fullPath)
	}

	router.onError((error, to) => {
		if (!isStaleAssetError(error)) return
		recover(to.fullPath)
	})

	// Vite fires this when its preload helper fails, and throws unless the event is
	// defaulted. Suppressing the throw without recovering would recreate the silent freeze,
	// so this path must always either reload or surface the blocking prompt.
	window.addEventListener('vite:preloadError', (event) => {
		event.preventDefault()
		recover(router.currentRoute.value.fullPath)
	})
}
